import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bug, ChevronDown, ChevronUp, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../../../ui/button';
import { StoredApplication } from '../../../utils/applicationStorage';

interface FilterDebugPanelProps {
    applications: StoredApplication[];
    filteredApplications: StoredApplication[];
}

export const FilterDebugPanel: React.FC<FilterDebugPanelProps> = ({
    applications,
    filteredApplications
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedApp, setSelectedApp] = useState<StoredApplication | null>(null);

    // Analyze all applications data
    const analyzeData = () => {
        const analysis = {
            total: applications.length,
            filtered: filteredApplications.length,
            fields: {
                experienceLevel: new Map<string, number>(),
                qualification: new Map<string, number>(),
                yearOfPassing: new Map<string, number>(),
                noticePeriod: new Map<string, number>(),
                totalExperience: new Map<string, number>(),
                skills: new Map<string, number>(),
            },
            missing: {
                experienceLevel: 0,
                qualification: 0,
                yearOfPassing: 0,
                noticePeriod: 0,
                totalExperience: 0,
                skills: 0,
            },
            inferred: {
                fresher: 0,
                experienced: 0,
            }
        };

        applications.forEach(app => {
            // Track field values
            const expLevel = (app.experienceLevel || '').trim();
            const qual = (app.qualification || '').trim();
            const year = (app.yearOfPassing || '').trim();
            const notice = (app.noticePeriod || '').trim();
            const totalExp = (app.totalExperience || app.experience || '').trim();

            if (expLevel) {
                analysis.fields.experienceLevel.set(expLevel, (analysis.fields.experienceLevel.get(expLevel) || 0) + 1);
            } else {
                analysis.missing.experienceLevel++;
                
                // Infer experience level
                const totalExpLower = totalExp.toLowerCase();
                const expMatch = totalExpLower.match(/(\d+)/);
                const yearsExp = expMatch ? parseInt(expMatch[1]) : 0;
                
                const isFresher = yearsExp === 0 || 
                    totalExpLower.includes('fresher') || 
                    totalExpLower.includes('0 year') ||
                    totalExpLower === '' ||
                    totalExpLower === '0';
                
                if (isFresher) {
                    analysis.inferred.fresher++;
                } else if (yearsExp > 0) {
                    analysis.inferred.experienced++;
                }
            }

            if (qual) {
                analysis.fields.qualification.set(qual, (analysis.fields.qualification.get(qual) || 0) + 1);
            } else {
                analysis.missing.qualification++;
            }

            if (year) {
                analysis.fields.yearOfPassing.set(year, (analysis.fields.yearOfPassing.get(year) || 0) + 1);
            } else {
                analysis.missing.yearOfPassing++;
            }

            if (notice) {
                analysis.fields.noticePeriod.set(notice, (analysis.fields.noticePeriod.get(notice) || 0) + 1);
            } else {
                analysis.missing.noticePeriod++;
            }

            if (totalExp) {
                analysis.fields.totalExperience.set(totalExp, (analysis.fields.totalExperience.get(totalExp) || 0) + 1);
            } else {
                analysis.missing.totalExperience++;
            }

            if (app.skills && app.skills.length > 0) {
                app.skills.forEach(skill => {
                    analysis.fields.skills.set(skill, (analysis.fields.skills.get(skill) || 0) + 1);
                });
            } else {
                analysis.missing.skills++;
            }
        });

        return analysis;
    };

    const analysis = analyzeData();

    const getStatusIcon = (value: number, total: number) => {
        const percentage = (value / total) * 100;
        if (percentage === 100) return <CheckCircle className="w-4 h-4 text-green-400" />;
        if (percentage >= 50) return <AlertCircle className="w-4 h-4 text-yellow-400" />;
        return <XCircle className="w-4 h-4 text-red-400" />;
    };

    return (
        <div className="fixed bottom-4 right-4 z-50">
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="mb-4 w-[600px] max-h-[600px] overflow-hidden glass-strong rounded-xl border border-primary/30 shadow-2xl flex flex-col"
                    >
                        {/* Header */}
                        <div className="px-4 py-3 border-b border-border/30 bg-primary/5">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Bug className="w-5 h-5 text-primary" />
                                    <h3 className="text-foreground">Filter Debug Panel</h3>
                                </div>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    <ChevronDown className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {/* Summary */}
                            <div className="glass rounded-lg p-3 border border-border/30">
                                <h4 className="text-sm text-foreground mb-2">Summary</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">Total Applications:</span>
                                        <span className="ml-2 text-foreground">{analysis.total}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Filtered:</span>
                                        <span className="ml-2 text-primary">{analysis.filtered}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Experience Level Analysis */}
                            <div className="glass rounded-lg p-3 border border-border/30">
                                <div className="flex items-center gap-2 mb-3">
                                    {getStatusIcon(
                                        analysis.total - analysis.missing.experienceLevel,
                                        analysis.total
                                    )}
                                    <h4 className="text-sm text-foreground">Experience Level</h4>
                                </div>
                                
                                <div className="space-y-2 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">Explicitly Set:</span>
                                        <span className="ml-2 text-foreground">{analysis.total - analysis.missing.experienceLevel}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Missing/Empty:</span>
                                        <span className="ml-2 text-red-400">{analysis.missing.experienceLevel}</span>
                                    </div>
                                    
                                    {analysis.fields.experienceLevel.size > 0 && (
                                        <div className="mt-2 pt-2 border-t border-border/30">
                                            <div className="text-muted-foreground text-xs mb-1">Explicit Values:</div>
                                            {Array.from(analysis.fields.experienceLevel.entries()).map(([value, count]) => (
                                                <div key={value} className="flex justify-between pl-2">
                                                    <span className="text-foreground">"{value}"</span>
                                                    <span className="text-muted-foreground">({count})</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {analysis.missing.experienceLevel > 0 && (
                                        <div className="mt-2 pt-2 border-t border-border/30">
                                            <div className="text-muted-foreground text-xs mb-1">Inferred from Experience:</div>
                                            <div className="flex justify-between pl-2">
                                                <span className="text-foreground">Fresher (inferred)</span>
                                                <span className="text-muted-foreground">({analysis.inferred.fresher})</span>
                                            </div>
                                            <div className="flex justify-between pl-2">
                                                <span className="text-foreground">Experienced (inferred)</span>
                                                <span className="text-muted-foreground">({analysis.inferred.experienced})</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Qualification Analysis */}
                            <div className="glass rounded-lg p-3 border border-border/30">
                                <div className="flex items-center gap-2 mb-3">
                                    {getStatusIcon(
                                        analysis.total - analysis.missing.qualification,
                                        analysis.total
                                    )}
                                    <h4 className="text-sm text-foreground">Qualification</h4>
                                </div>
                                
                                <div className="space-y-2 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">With Data:</span>
                                        <span className="ml-2 text-foreground">{analysis.total - analysis.missing.qualification}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Missing:</span>
                                        <span className="ml-2 text-red-400">{analysis.missing.qualification}</span>
                                    </div>
                                    
                                    {analysis.fields.qualification.size > 0 && (
                                        <div className="mt-2 pt-2 border-t border-border/30 max-h-32 overflow-y-auto">
                                            <div className="text-muted-foreground text-xs mb-1">Values:</div>
                                            {Array.from(analysis.fields.qualification.entries())
                                                .sort((a, b) => b[1] - a[1])
                                                .map(([value, count]) => (
                                                    <div key={value} className="flex justify-between pl-2">
                                                        <span className="text-foreground truncate">"{value}"</span>
                                                        <span className="text-muted-foreground">({count})</span>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Skills Analysis */}
                            <div className="glass rounded-lg p-3 border border-border/30">
                                <div className="flex items-center gap-2 mb-3">
                                    {getStatusIcon(
                                        analysis.total - analysis.missing.skills,
                                        analysis.total
                                    )}
                                    <h4 className="text-sm text-foreground">Skills</h4>
                                </div>
                                
                                <div className="space-y-2 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">With Skills:</span>
                                        <span className="ml-2 text-foreground">{analysis.total - analysis.missing.skills}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Without Skills:</span>
                                        <span className="ml-2 text-red-400">{analysis.missing.skills}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Unique Skills:</span>
                                        <span className="ml-2 text-foreground">{analysis.fields.skills.size}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Individual Application Inspector */}
                            <div className="glass rounded-lg p-3 border border-border/30">
                                <h4 className="text-sm text-foreground mb-2">Application Inspector</h4>
                                <select
                                    className="w-full px-3 py-2 rounded-lg glass border border-border/30 text-foreground text-sm"
                                    value={selectedApp?.id || ''}
                                    onChange={(e) => {
                                        const app = applications.find(a => a.id === e.target.value);
                                        setSelectedApp(app || null);
                                    }}
                                >
                                    <option value="">Select an application...</option>
                                    {applications.map(app => (
                                        <option key={app.id} value={app.id}>
                                            {app.name} - {app.position}
                                        </option>
                                    ))}
                                </select>

                                {selectedApp && (
                                    <div className="mt-3 p-3 glass rounded-lg text-xs space-y-2 max-h-48 overflow-y-auto">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <span className="text-muted-foreground">Name:</span>
                                                <span className="ml-2 text-foreground">{selectedApp.name}</span>
                                            </div>
                                            <div>
                                                <span className="text-muted-foreground">Position:</span>
                                                <span className="ml-2 text-foreground">{selectedApp.position}</span>
                                            </div>
                                        </div>
                                        <div className="pt-2 border-t border-border/30">
                                            <div className="text-muted-foreground mb-1">Filter Fields:</div>
                                            <div className="pl-2 space-y-1">
                                                <div>
                                                    <span className="text-cyan-400">experienceLevel:</span>
                                                    <span className="ml-2 text-foreground">"{selectedApp.experienceLevel || '(empty)'}"</span>
                                                </div>
                                                <div>
                                                    <span className="text-cyan-400">totalExperience:</span>
                                                    <span className="ml-2 text-foreground">"{selectedApp.totalExperience || '(empty)'}"</span>
                                                </div>
                                                <div>
                                                    <span className="text-cyan-400">experience:</span>
                                                    <span className="ml-2 text-foreground">"{selectedApp.experience || '(empty)'}"</span>
                                                </div>
                                                <div>
                                                    <span className="text-cyan-400">qualification:</span>
                                                    <span className="ml-2 text-foreground">"{selectedApp.qualification || '(empty)'}"</span>
                                                </div>
                                                <div>
                                                    <span className="text-cyan-400">yearOfPassing:</span>
                                                    <span className="ml-2 text-foreground">"{selectedApp.yearOfPassing || '(empty)'}"</span>
                                                </div>
                                                <div>
                                                    <span className="text-cyan-400">noticePeriod:</span>
                                                    <span className="ml-2 text-foreground">"{selectedApp.noticePeriod || '(empty)'}"</span>
                                                </div>
                                                <div>
                                                    <span className="text-cyan-400">skills:</span>
                                                    <span className="ml-2 text-foreground">
                                                        {selectedApp.skills && selectedApp.skills.length > 0 
                                                            ? `[${selectedApp.skills.join(', ')}]`
                                                            : '(empty)'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Toggle Button */}
            <Button
                onClick={() => setIsOpen(!isOpen)}
                className={`${
                    isOpen 
                        ? 'bg-primary/20 text-primary border-primary/50' 
                        : 'glass-strong border-border/30'
                } shadow-lg`}
                variant="outline"
            >
                <Bug className="w-4 h-4 mr-2" />
                {isOpen ? 'Hide' : 'Debug'} Filters
                {isOpen ? <ChevronDown className="w-4 h-4 ml-2" /> : <ChevronUp className="w-4 h-4 ml-2" />}
            </Button>
        </div>
    );
};
